import React, { useState, useEffect } from 'react';
import Select from 'react-select';
import CurrencyInput from 'react-currency-input-field';
import Swal from 'sweetalert2';

import DatePicker from '../../../../components/utils/datepicker';
import { autoCompleteFornecedores, autoCompleteProdutos } from '../../../../components/utils/autocompletes';
import { FaRegSave, FaTimes, FaPlus, FaTrashAlt } from 'react-icons/fa';

const NovoPedidoCompra = ({ pedido, onClose, onSave }) => {
  const [tituloModal, setTituloModal] = useState('');
  const [pedidoAberto, setPedidoAberto] = useState(false);
  const [activeTab, setActiveTab] = useState('pedido');
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);

  const { fornecedoresOptions, handleAutoCompleteFornecedores, handleFocusFornecedores } = autoCompleteFornecedores();
  const { produtosOptions, handleAutoCompleteProdutos, handleFocusProdutos } = autoCompleteProdutos();

  const [formData, setFormData] = useState({
    id: '',
    numero: '',
    fornecedor: null,
    data_pedido: new Date(),
    data_entrega: null,
    itens: [],
    pagamentos: [],
    observacao: ''
  });

  const customStyles = {
    menu: (provided) => ({
      ...provided,
      whiteSpace: 'pre-line',
    }),
    option: (provided) => ({
      ...provided,
      whiteSpace: 'pre-wrap',
      wordBreak: 'break-word',
    }),
  };

  useEffect(() => {
    const handleResize = () => {
      setWindowWidth(window.innerWidth);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    if (pedido) {
      setFormData({
        id: pedido.id || '',
        numero: pedido.numero || '',
        fornecedor: pedido.fornecedor ? { value: pedido.fornecedor.id, label: pedido.fornecedor.nome } : null,
        data_pedido: pedido.data_pedido ? new Date(pedido.data_pedido + 'T00:00:00') : new Date(),
        data_entrega: pedido.data_entrega ? new Date(pedido.data_entrega + 'T00:00:00') : null,
        itens: pedido.itens || [],
        pagamentos: pedido.pagamentos || [],
        observacao: pedido.observacao || ''
      });
      setTituloModal('Editar Pedido de Compra');
      setPedidoAberto(true);
    } else {
      setTituloModal('Novo Pedido de Compra');
      setPedidoAberto(false);
    }
  }, [pedido]);

  const handleFornecedorSelect = (selectedOption) => {
    setFormData(prev => ({ ...prev, fornecedor: selectedOption }));
  };

  const handleAbrirPedido = () => {
    if (!formData.fornecedor) {
      Swal.fire({
        title: 'Atenção',
        text: 'Selecione um fornecedor antes de abrir o pedido.',
        icon: 'warning',
        confirmButtonColor: '#0d6efd'
      });
      return;
    }
    setPedidoAberto(true);
  };

  const handleAdicionarItem = () => {
    const novoItem = {
      id: `temp_${Date.now()}`,
      produto: null,
      quantidade: 1,
      valor: '',
      imposto: '',
      desconto: '',
      valor_pago: '',
      editar: true
    };
    setFormData(prev => ({
      ...prev,
      itens: [...prev.itens, novoItem]
    }));
  };

  const handleChangeItem = (itemId, field, value) => {
    setFormData(prev => ({
      ...prev,
      itens: prev.itens.map(item => {
        if (item.id === itemId) {
          const updatedItem = { ...item, [field]: value };

          if (field === 'produto' && value) {
            updatedItem.valor = value.preco || '';
          }

          const valor = parseFloat(updatedItem.valor) || 0;
          const quantidade = parseFloat(updatedItem.quantidade) || 0;
          const imposto = parseFloat(updatedItem.imposto) || 0;
          const desconto = parseFloat(updatedItem.desconto) || 0;

          const subtotal = valor * quantidade;
          updatedItem.valor_pago = (subtotal + imposto - desconto).toFixed(2);

          return updatedItem;
        }
        return item;
      })
    }));
  };

  const handleSalvarItem = (itemId) => {
    setFormData(prev => ({
      ...prev,
      itens: prev.itens.map(item =>
        item.id === itemId ? { ...item, editar: false } : item
      )
    }));
  };

  const handleDeleteItem = (itemId) => {
    Swal.fire({
      title: 'Confirmar exclusão',
      text: 'Deseja realmente excluir este item?',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#dc3545',
      cancelButtonColor: '#6c757d',
      confirmButtonText: 'Sim, excluir',
      cancelButtonText: 'Cancelar'
    }).then((result) => {
      if (result.isConfirmed) {
        setFormData(prev => ({
          ...prev,
          itens: prev.itens.filter(item => item.id !== itemId)
        }));
      }
    });
  };

  const handleAdicionarPagamento = () => {
    const novoPagamento = {
      id: `temp_${Date.now()}`,
      tipo_pagamento: 'pagamento_boleto',
      valor: '',
      data_vencimento: null,
      informacoes: ''
    };
    setFormData(prev => ({
      ...prev,
      pagamentos: [...prev.pagamentos, novoPagamento]
    }));
  };

  const handleChangePagamento = (pagamentoId, field, value) => {
    setFormData(prev => ({
      ...prev,
      pagamentos: prev.pagamentos.map(pag =>
        pag.id === pagamentoId ? { ...pag, [field]: value } : pag
      )
    }));
  };

  const handleDeletePagamento = (pagamentoId) => {
    Swal.fire({
      title: 'Confirmar exclusão',
      text: 'Deseja realmente excluir este pagamento?',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#dc3545',
      cancelButtonColor: '#6c757d',
      confirmButtonText: 'Sim, excluir',
      cancelButtonText: 'Cancelar'
    }).then((result) => {
      if (result.isConfirmed) {
        setFormData(prev => ({
          ...prev,
          pagamentos: prev.pagamentos.filter(pag => pag.id !== pagamentoId)
        }));
      }
    });
  };

  const calcularTotalItens = () => {
    const total = formData.itens.reduce((acc, item) => {
      return acc + (parseFloat(item.valor_pago) || 0);
    }, 0);
    return total.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
  };

  const calcularTotalPagamentos = () => {
    const total = formData.pagamentos.reduce((acc, pag) => {
      return acc + (parseFloat(pag.valor) || 0);
    }, 0);
    return total.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    Swal.fire({
      title: 'Em desenvolvimento',
      text: 'O salvamento será implementado quando integrarmos com o backend.',
      icon: 'info',
      confirmButtonColor: '#0d6efd'
    });
  };

  return (
    <div className="modal fade show" tabIndex="-1" style={{ display: 'block', backgroundColor: 'rgba(0,0,0,0.5)' }}>
      <div className="modal-dialog modal-xl modal-dialog-scrollable modal-dialog-centered" style={{ maxWidth: windowWidth < 1128 ? '95%' : '70%', margin: '0.5rem auto' }}>
        <div className="modal-content" onClick={(e) => e.stopPropagation()}>
          <div className="modal-header">
            <h4 className="fw-bold mt-2">{tituloModal}</h4>
            <button type="button" className="btn-close" onClick={onClose} aria-label="Close"></button>
          </div>

          <div className="modal-body">
            <form onSubmit={handleSubmit}>
              <div className="row mt-3">
                <div className="col-lg-6 col-12">
                  <label className="form-label fw-bold">Fornecedor</label>
                  <Select
                    value={formData.fornecedor}
                    onChange={handleFornecedorSelect}
                    onInputChange={(value) => {
                      handleAutoCompleteFornecedores(value);
                    }}
                    onFocus={handleFocusFornecedores}
                    options={fornecedoresOptions}
                    styles={customStyles}
                    isSearchable
                    isClearable
                    placeholder="Buscar fornecedor..."
                    noOptionsMessage={() => 'Nenhum fornecedor encontrado'}
                    isDisabled={pedidoAberto}
                  />
                </div>
                <div className="col-lg-3 col-md-6 col-12 mt-lg-0 mt-3">
                  <label className="form-label fw-bold">Data do Pedido</label>
                  <DatePicker
                    selected={formData.data_pedido}
                    onChange={(date) => setFormData(prev => ({ ...prev, data_pedido: date }))}
                    placeholderText="dd/mm/aaaa"
                    disabled={pedidoAberto}
                  />
                </div>
                <div className="col-lg-3 col-md-6 col-12 mt-lg-0 mt-3">
                  <label className="form-label fw-bold">Data da Entrega</label>
                  <DatePicker
                    selected={formData.data_entrega}
                    onChange={(date) => setFormData(prev => ({ ...prev, data_entrega: date }))}
                    placeholderText="dd/mm/aaaa"
                    disabled={pedidoAberto}
                  />
                </div>
              </div>
              <div className="row mt-3">
                <div className="col-lg-3 col-md-6 col-12">
                  <label className="form-label fw-bold">Nº</label>
                  <input
                    type="text"
                    className="form-control"
                    value={formData.numero}
                    onChange={(e) => setFormData(prev => ({ ...prev, numero: e.target.value }))}
                    placeholder="Número do pedido"
                    disabled={pedidoAberto}
                  />
                </div>
                <div className="col-lg-1 col-md-6 col-12 mt-lg-0 mt-3 d-flex align-items-end">
                  {!pedidoAberto && (
                    <button
                      type="button"
                      className="btn btn-primary w-100"
                      onClick={handleAbrirPedido}
                    >
                      Abrir
                    </button>
                  )}
                </div>
              </div>

              {pedidoAberto && (
                <>
                  <ul className="nav nav-tabs mt-4">
                    <li className="nav-item">
                      <a
                        className={activeTab === 'pedido' ? 'nav-link active' : 'nav-link'}
                        style={{ cursor: 'pointer' }}
                        onClick={() => setActiveTab('pedido')}
                      >
                        Pedido
                      </a>
                    </li>
                    <li className="nav-item">
                      <a
                        className={activeTab === 'pagamentos' ? 'nav-link active' : 'nav-link'}
                        style={{ cursor: 'pointer' }}
                        onClick={() => setActiveTab('pagamentos')}
                      >
                        Formas de Pagamento
                      </a>
                    </li>
                    <li className="nav-item">
                      <a
                        className={activeTab === 'observacoes' ? 'nav-link active' : 'nav-link'}
                        style={{ cursor: 'pointer' }}
                        onClick={() => setActiveTab('observacoes')}
                      >
                        Observações
                      </a>
                    </li>
                  </ul>

                  {activeTab === 'pedido' && (
                    <div>
                      <div className="row mt-4">
                        <div className="col-12">
                          <div className="table-responsive" style={{ maxHeight: '45vh' }}>
                            <table className="table">
                              <thead className="table-custom-primary">
                                <tr>
                                  <th>Produto</th>
                                  <th>Qtd.</th>
                                  <th className="text-nowrap">Valor (R$)</th>
                                  <th className="text-nowrap">Imposto (R$)</th>
                                  <th className="text-nowrap">Desconto (R$)</th>
                                  <th className="text-nowrap">Valor Pago (R$)</th>
                                  <th className="text-center">Ações</th>
                                </tr>
                              </thead>
                              <tbody>
                                {formData.itens.map((item) => (
                                    <tr key={item.id}>
                                      <td className="text-nowrap">
                                        <div style={{ width: '300px' }}>
                                          <Select
                                            options={produtosOptions}
                                            styles={customStyles}
                                            onInputChange={(value) => {
                                              handleAutoCompleteProdutos(value);
                                            }}
                                            onFocus={handleFocusProdutos}
                                            onChange={(value) => handleChangeItem(item.id, 'produto', value)}
                                            placeholder="Selecione o produto"
                                            isClearable
                                            menuPlacement="auto"
                                            menuPosition="fixed"
                                            isDisabled={!item.editar}
                                            noOptionsMessage={() => 'Nenhum produto encontrado'}
                                            value={item.produto}
                                          />
                                        </div>
                                      </td>
                                      <td>
                                        <input
                                          type="number"
                                          className="form-control"
                                          style={{ width: '80px' }}
                                          value={item.quantidade}
                                          onChange={(e) => handleChangeItem(item.id, 'quantidade', e.target.value)}
                                          disabled={!item.editar}
                                          min="1"
                                        />
                                      </td>
                                      <td className="text-nowrap">
                                        <CurrencyInput
                                          placeholder="R$ 0,00"
                                          value={item.valor}
                                          decimalsLimit={2}
                                          decimalSeparator=","
                                          groupSeparator="."
                                          prefix="R$ "
                                          className="form-control"
                                          style={{ width: '130px' }}
                                          disabled={!item.editar}
                                          onValueChange={(value) => handleChangeItem(item.id, 'valor', value)}
                                        />
                                      </td>
                                      <td className="text-nowrap">
                                        <CurrencyInput
                                          placeholder="R$ 0,00"
                                          value={item.imposto}
                                          decimalsLimit={2}
                                          decimalSeparator=","
                                          groupSeparator="."
                                          prefix="R$ "
                                          className="form-control"
                                          style={{ width: '130px' }}
                                          disabled={!item.editar}
                                          onValueChange={(value) => handleChangeItem(item.id, 'imposto', value)}
                                        />
                                      </td>
                                      <td className="text-nowrap">
                                        <CurrencyInput
                                          placeholder="R$ 0,00"
                                          value={item.desconto}
                                          decimalsLimit={2}
                                          decimalSeparator=","
                                          groupSeparator="."
                                          prefix="R$ "
                                          className="form-control"
                                          style={{ width: '130px' }}
                                          disabled={!item.editar}
                                          onValueChange={(value) => handleChangeItem(item.id, 'desconto', value)}
                                        />
                                      </td>
                                      <td className="text-nowrap">
                                        <CurrencyInput
                                          placeholder="R$ 0,00"
                                          value={item.valor_pago}
                                          decimalsLimit={2}
                                          decimalSeparator=","
                                          groupSeparator="."
                                          prefix="R$ "
                                          className="form-control"
                                          style={{ width: '130px' }}
                                          disabled
                                        />
                                      </td>
                                      <td className="text-center text-nowrap">
                                        {item.editar && (
                                          <button
                                            className="btn btn-custom-secondary btn-sm"
                                            type="button"
                                            onClick={() => handleSalvarItem(item.id)}
                                            title="Salvar"
                                          >
                                            <FaRegSave />
                                          </button>
                                        )}
                                        <button
                                          className="btn btn-custom-secondary btn-sm ms-2"
                                          type="button"
                                          onClick={() => handleDeleteItem(item.id)}
                                          title="Excluir"
                                        >
                                          <FaTrashAlt />
                                        </button>
                                      </td>
                                    </tr>
                                  ))}
                              </tbody>
                            </table>
                          </div>
                        </div>
                      </div>
                      <div className="row mt-3">
                        <div className="col-12 d-flex justify-content-end">
                          <button
                            className="btn btn-outline-primary"
                            type="button"
                            onClick={handleAdicionarItem}
                          >
                            <FaPlus className="me-2" />
                            Adicionar Produto
                          </button>
                        </div>
                      </div>
                    </div>
                  )}

                  {activeTab === 'pagamentos' && (
                    <div>
                      <div className="row mt-4">
                        <div className="col-12">
                          <div className="table-responsive" style={{ maxHeight: '45vh' }}>
                            <table className="table">
                              <thead className="table-custom-primary">
                                <tr>
                                  <th className="text-nowrap">Forma de Pagamento</th>
                                  <th className="text-nowrap">Valor (R$)</th>
                                  <th className="text-nowrap">Data Vencimento</th>
                                  <th>Informações</th>
                                  <th className="text-center">Ações</th>
                                </tr>
                              </thead>
                              <tbody>
                                {formData.pagamentos.map((pag) => (
                                    <tr key={pag.id}>
                                      <td className="text-nowrap">
                                        <select
                                          className="form-select"
                                          style={{ width: '200px' }}
                                          value={pag.tipo_pagamento}
                                          onChange={(e) => handleChangePagamento(pag.id, 'tipo_pagamento', e.target.value)}
                                        >
                                          <option value="pagamento_boleto">Boleto</option>
                                          <option value="pagamento_pix">Pix</option>
                                          <option value="pagamento_ted">TED</option>
                                          <option value="pagamento_doc">DOC</option>
                                          <option value="pagamento_dinheiro">Dinheiro</option>
                                          <option value="pagamento_credito">Cartão Crédito</option>
                                          <option value="pagamento_debito">Cartão Débito</option>
                                        </select>
                                      </td>
                                      <td className="text-nowrap">
                                        <CurrencyInput
                                          placeholder="R$ 0,00"
                                          value={pag.valor}
                                          decimalsLimit={2}
                                          decimalSeparator=","
                                          groupSeparator="."
                                          prefix="R$ "
                                          className="form-control"
                                          style={{ width: '150px' }}
                                          onValueChange={(value) => handleChangePagamento(pag.id, 'valor', value)}
                                        />
                                      </td>
                                      <td className="text-nowrap">
                                        <div style={{ width: '150px' }}>
                                          <DatePicker
                                            selected={pag.data_vencimento}
                                            onChange={(date) => handleChangePagamento(pag.id, 'data_vencimento', date)}
                                            placeholderText="dd/mm/aaaa"
                                          />
                                        </div>
                                      </td>
                                      <td>
                                        <input
                                          type="text"
                                          className="form-control"
                                          style={{ width: '200px' }}
                                          value={pag.informacoes}
                                          onChange={(e) => handleChangePagamento(pag.id, 'informacoes', e.target.value)}
                                          placeholder="Informações adicionais"
                                        />
                                      </td>
                                      <td className="text-center text-nowrap">
                                        <button
                                          className="btn btn-custom-secondary btn-sm"
                                          type="button"
                                          onClick={() => handleDeletePagamento(pag.id)}
                                          title="Excluir"
                                        >
                                          <FaTrashAlt />
                                        </button>
                                      </td>
                                    </tr>
                                  ))}
                              </tbody>
                            </table>
                          </div>
                        </div>
                      </div>
                      <div className="row mt-3">
                        <div className="col-12 d-flex justify-content-end">
                          <button
                            className="btn btn-outline-primary"
                            type="button"
                            onClick={handleAdicionarPagamento}
                          >
                            <FaPlus className="me-2" />
                            Adicionar Pagamento
                          </button>
                        </div>
                      </div>
                    </div>
                  )}

                  {activeTab === 'observacoes' && (
                    <div className="row mt-4">
                      <div className="col-12">
                        <label className="form-label fw-bold">Observações</label>
                        <textarea
                          className="form-control"
                          rows="6"
                          value={formData.observacao}
                          onChange={(e) => setFormData(prev => ({ ...prev, observacao: e.target.value }))}
                        />
                      </div>
                    </div>
                  )}
                </>
              )}
            </form>
          </div>

          <div className="modal-footer">
            <button type="button" className="btn btn-outline-danger" onClick={onClose}>
              <FaTimes /> Fechar
            </button>
            {pedidoAberto && (
              <button type="button" className="btn btn-custom-primary ms-2" onClick={handleSubmit}>
                <FaRegSave className="me-2" />
                Salvar
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default NovoPedidoCompra;
